import { AfterViewInit, ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, OnDestroy, signal, viewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    Annotation, AnnotationHandle, annotationBounds, annotationHandles, ASPECT_RATIOS, AspectMode, CropTarget, drawAnnotation, EditorState, EditorTool,
    fitAspect, hitAnnotation, ImageEditorData, ImageEditorResult, mapAnnotation, mapRect, MARKUP_COLORS, MarkupTool, moveHandle, orientedSize,
    OrientationOp, Point, Rect, renderOriented, SizePreset, strokeWidth, textFont, textSize, translateAnnotation
} from './image-editor.model';
import { ImageFit, ImageFrame, LISTING_ASPECT_RATIO } from '../../listing-image/listing-image.model';

const MIN_CROP = 32; //image pixels
const HANDLE_HIT = 22; //screen pixels
const STAGE_PADDING = 24; //screen pixels
const MAX_ZOOM = 8;
const MOVE_THRESHOLD = 4; //screen pixels before a tap on a selected annotation becomes a move
const DOUBLE_TAP_MS = 400;
const OUTPUT_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

//scale includes the zoom; baseScale = scale at zoom 1 (region fits the stage)
interface View { scale: number; baseScale: number; offsetX: number; offsetY: number; region: Rect; cssW: number; cssH: number; }
type TextAnnotation = Extract<Annotation, { kind: 'text' }>;

//crop-move/crop-resize edit the crop or the listing frame (target), always inside bounds
type FrameTarget = 'crop' | 'frame';
type Drag =
    | { kind: 'crop-move'; target: FrameTarget; bounds: Rect; start: Point; startCrop: Rect }
    | { kind: 'crop-resize'; target: FrameTarget; bounds: Rect; ratio?: number; anchor: Point; dirX: number; dirY: number }
    | { kind: 'draw'; annotation: Annotation }
    | { kind: 'text'; at: Point }
    | { kind: 'move'; index: number; start: Point; original: Annotation; moved: boolean; editText: boolean }
    | { kind: 'handle'; index: number; handle: AnnotationHandle; original: Annotation }
    | { kind: 'pan'; startClient: Point; startPan: Point };

@Component({
    selector: 'app-image-editor-dialog',
    templateUrl: './image-editor-dialog.component.html',
    styleUrls: ['./image-editor-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule, MatTooltipModule],
    host: { '(document:keydown)': 'onKeydown($event)', '(document:keyup)': 'onKeyup($event)' }
})
export class ImageEditorDialogComponent implements AfterViewInit, OnDestroy {
    private readonly data = inject<ImageEditorData>(MAT_DIALOG_DATA);
    private readonly dialogRef = inject<MatDialogRef<ImageEditorDialogComponent, ImageEditorResult>>(MatDialogRef);

    private readonly stageRef = viewChild.required<ElementRef<HTMLDivElement>>('stage');
    private readonly canvasRef = viewChild.required<ElementRef<HTMLCanvasElement>>('canvas');
    private readonly textInputRef = viewChild.required<ElementRef<HTMLInputElement>>('textInput');

    readonly title = this.data.title || 'Edit photo';
    readonly listingFrameEnabled = !!this.data.listingFrameEnabled;
    readonly colors = MARKUP_COLORS;
    readonly sizes: SizePreset[] = ['S', 'M', 'L'];
    readonly aspects: { mode: AspectMode; label: string; tooltip: string }[] = [
        { mode: 'free', label: 'Free', tooltip: 'Free crop' },
        { mode: '4:3', label: '4:3', tooltip: 'Fits the listing slider' }
    ];
    readonly cropTargets: { target: CropTarget; label: string; tooltip: string }[] = [
        { target: 'photo', label: 'Crop photo', tooltip: 'Cut the photo itself' },
        { target: 'frame', label: 'Listing frame', tooltip: 'Choose the part shown in the listing slider and cards (the photo stays complete)' }
    ];
    readonly fits: { fit: ImageFit; label: string; tooltip: string }[] = [
        { fit: 'cover', label: 'Fill box', tooltip: 'The frame fills the listing box' },
        { fit: 'contain', label: 'Whole photo', tooltip: 'Show the whole frame/photo on a blurred background, nothing is cut off' }
    ];
    readonly markupTools: { tool: MarkupTool; icon: string; label: string }[] = [
        { tool: 'select', icon: 'pan_tool_alt', label: 'Select: tap markup to move, change or delete it, double tap a text to edit it' },
        { tool: 'arrow', icon: 'arrow_outward', label: 'Arrow' },
        { tool: 'ellipse', icon: 'radio_button_unchecked', label: 'Circle' },
        { tool: 'rect', icon: 'crop_square', label: 'Rectangle' },
        { tool: 'pen', icon: 'gesture', label: 'Freehand' },
        { tool: 'text', icon: 'title', label: 'Text' }
    ];

    readonly tool = signal<EditorTool>('crop');
    readonly markupTool = signal<MarkupTool>('arrow');
    readonly color = signal(MARKUP_COLORS[0]);
    readonly size = signal<SizePreset>('M');
    readonly aspect = signal<AspectMode>('free');
    readonly cropTarget = signal<CropTarget>('photo');
    readonly zoom = signal(1);
    readonly selected = signal<number | undefined>(undefined);
    readonly loading = signal(true);
    readonly loadError = signal(false);
    readonly saving = signal(false);
    //click and at in image pixels (at = where the text is drawn), left/top/width/fontPx in screen pixels; editIndex: text being edited
    readonly textDraft = signal<{ click: Point; size: number; color: string; at: Point; left: number; top: number; width: number; fontPx: number; editIndex?: number } | undefined>(undefined);
    //the text input always uses 16px (smaller fonts make iOS zoom into the page on focus) and is scaled to the text size instead
    readonly textBox = computed(() => {
        const d = this.textDraft();
        if (!d) return undefined;
        const scale = d.fontPx / 16;
        return { left: d.left, top: d.top, width: d.width / scale, transform: `scale(${scale})`, color: d.color };
    });

    private readonly history = signal<EditorState[]>([]);
    private readonly historyIndex = signal(0);
    readonly state = computed<EditorState | undefined>(() => this.history()[this.historyIndex()]);
    readonly canUndo = computed(() => this.historyIndex() > 0);
    readonly canRedo = computed(() => this.historyIndex() < this.history().length - 1);
    //compared by value, so undoing or reverting everything does not count as a change
    readonly isDirty = computed(() => this.historyIndex() > 0 && JSON.stringify(this.state()) !== JSON.stringify(this.history()[0]));
    readonly hasMarkup = computed(() => (this.state()?.annotations.length ?? 0) > 0);
    //marked up photos are kept as a copy by default, so the original stays untouched
    readonly saveAsCopyByDefault = this.hasMarkup;
    readonly selectedAnnotation = computed(() => {
        const index = this.selected();
        return index === undefined ? undefined : this.state()?.annotations[index];
    });

    private source?: ImageBitmap;
    private oriented?: { key: string; canvas: HTMLCanvasElement };
    private view?: View;
    private drag?: Drag;
    private liveRect?: Rect; //crop or listing frame while dragging
    private liveAnnotation?: { index: number; annotation: Annotation }; //selected annotation while moving
    private pan: Point = { x: 0, y: 0 }; //screen pixels, relative to the centered position
    private pointers = new Map<number, Point>(); //active pointers (client coordinates)
    private pinch?: { startDistance: number; startZoom: number; anchor: Point };
    private gestureLock = false; //after a pinch until all fingers are up: no drawing with the remaining finger
    private spacePressed = false; //desktop: space + drag pans
    private lastTap?: { index: number; time: number };
    private resizeObserver?: ResizeObserver;
    private frame = 0;
    private measureContext?: CanvasRenderingContext2D;

    constructor() {
        effect(() => {
            this.state();
            this.tool();
            this.cropTarget();
            this.zoom();
            this.selected();
            this.scheduleRender();
        });
    }

    ngAfterViewInit(): void {
        this.resizeObserver = new ResizeObserver(() => this.resizeCanvas());
        this.resizeObserver.observe(this.stageRef().nativeElement);
        createImageBitmap(this.data.file).then(bitmap => {
            this.source = bitmap;
            const f = this.data.frame;
            const listingFrame = f ? { x: f.x * bitmap.width, y: f.y * bitmap.height, w: f.w * bitmap.width, h: f.h * bitmap.height } : null;
            this.history.set([{
                quarterTurns: 0, flipH: false, flipV: false, crop: { x: 0, y: 0, w: bitmap.width, h: bitmap.height }, annotations: [],
                listingFrame, fit: this.data.fit ?? 'cover'
            }]);
            this.historyIndex.set(0);
            this.loading.set(false);
        }).catch(err => {
            console.error('Loading image failed', err);
            this.loading.set(false);
            this.loadError.set(true);
        });
    }

    ngOnDestroy(): void {
        this.resizeObserver?.disconnect();
        cancelAnimationFrame(this.frame);
        this.source?.close();
    }

    //#region history
    private push(state: EditorState): void {
        const history = this.history().slice(0, this.historyIndex() + 1);
        history.push(state);
        this.history.set(history);
        this.historyIndex.set(history.length - 1);
    }

    //replaces one annotation (undefined removes it)
    private pushAnnotation(index: number, annotation: Annotation | undefined): void {
        const s = this.state();
        if (!s) return;
        const annotations = s.annotations.slice();
        if (annotation) annotations[index] = annotation;
        else annotations.splice(index, 1);
        this.push({ ...s, annotations });
    }

    undo(): void {
        this.selected.set(undefined);
        if (this.canUndo()) this.historyIndex.update(i => i - 1);
    }

    redo(): void {
        this.selected.set(undefined);
        if (this.canRedo()) this.historyIndex.update(i => i + 1);
    }

    revert(): void {
        this.selected.set(undefined);
        this.aspect.set('free');
        this.push(this.history()[0]);
    }
    //#endregion

    //#region tools
    setTool(tool: EditorTool): void {
        this.commitText();
        this.selected.set(undefined);
        this.tool.set(tool);
        this.fitView();
    }

    setMarkupTool(tool: MarkupTool): void {
        this.commitText();
        if (tool !== 'select') this.selected.set(undefined);
        this.markupTool.set(tool);
    }

    //sets the color for new markup and changes the selected one
    setColor(color: string): void {
        this.color.set(color);
        const index = this.selected();
        const a = this.selectedAnnotation();
        if (index !== undefined && a && a.color !== color) this.pushAnnotation(index, { ...a, color });
    }

    //sets the size for new markup and changes the selected one
    setSize(size: SizePreset): void {
        this.size.set(size);
        const s = this.state();
        const index = this.selected();
        const a = this.selectedAnnotation();
        if (!s || index === undefined || !a) return;
        const { w, h } = this.orientedSizeOf(s);
        if (a.kind === 'text') this.pushAnnotation(index, { ...a, size: textSize(size, w, h) });
        else this.pushAnnotation(index, { ...a, width: strokeWidth(size, w, h) });
    }

    deleteSelected(): void {
        const index = this.selected();
        if (index === undefined) return;
        this.selected.set(undefined);
        this.pushAnnotation(index, undefined);
    }

    //opens the text input on the selected text (called within a tap, see openText)
    editSelectedText(): void {
        const index = this.selected();
        const a = this.selectedAnnotation();
        if (index === undefined || a?.kind !== 'text') return;
        this.openText(a.at, a.size, a.color, { index, text: a.text });
    }

    setAspect(mode: AspectMode): void {
        this.aspect.set(mode);
        const ratio = ASPECT_RATIOS[mode];
        const s = this.state();
        if (!s || !ratio) return;
        this.push(withValidFrame({ ...s, crop: fitAspect(s.crop, ratio) }));
    }

    setCropTarget(target: CropTarget): void {
        this.cropTarget.set(target);
        this.fitView();
    }

    setFit(fit: ImageFit): void {
        const s = this.state();
        if (s && s.fit !== fit) this.push({ ...s, fit });
    }

    //back to the whole image centered
    resetFrame(): void {
        const s = this.state();
        if (s?.listingFrame) this.push({ ...s, listingFrame: null });
    }

    rotate(clockwise: boolean): void {
        this.applyOrientation(clockwise ? 'cw' : 'ccw');
    }

    flip(horizontal: boolean): void {
        this.applyOrientation(horizontal ? 'flipH' : 'flipV');
    }

    //rotating/flipping maps crop and markup along, so nothing has to be redone
    private applyOrientation(op: OrientationOp): void {
        const s = this.state();
        if (!s || !this.source) return;
        const { w: W, h: H } = this.orientedSizeOf(s);
        const rotating = op === 'cw' || op === 'ccw';
        const next: EditorState = {
            //flips are applied after the rotation, so a rotation swaps their axes
            quarterTurns: op === 'cw' ? (s.quarterTurns + 1) % 4 : op === 'ccw' ? (s.quarterTurns + 3) % 4 : s.quarterTurns,
            flipH: rotating ? s.flipV : op === 'flipH' ? !s.flipH : s.flipH,
            flipV: rotating ? s.flipH : op === 'flipV' ? !s.flipV : s.flipV,
            crop: mapRect(s.crop, op, W, H),
            annotations: s.annotations.map(a => mapAnnotation(a, op, W, H)),
            //a rotated 4:3 frame would be 3:4: rotating resets it, flipping keeps it
            listingFrame: s.listingFrame && !rotating ? mapRect(s.listingFrame, op, W, H) : null,
            fit: s.fit
        };
        const ratio = ASPECT_RATIOS[this.aspect()];
        if (rotating && ratio) {
            const size = this.orientedSizeOf(next);
            next.crop = fitAspect({ x: 0, y: 0, w: size.w, h: size.h }, ratio);
        }
        this.fitView();
        this.push(withValidFrame(next));
    }

    clearMarkup(): void {
        const s = this.state();
        this.selected.set(undefined);
        if (s && s.annotations.length) this.push({ ...s, annotations: [] });
    }
    //#endregion

    //#region zoom
    zoomIn(): void {
        this.zoomAtCenter(this.zoom() * 1.5);
    }

    zoomOut(): void {
        this.zoomAtCenter(this.zoom() / 1.5);
    }

    //whole region visible again
    fitView(): void {
        this.pan = { x: 0, y: 0 };
        this.zoom.set(1);
        this.scheduleRender();
    }

    onWheel(e: WheelEvent): void {
        if (!this.view) return;
        e.preventDefault();
        const point = this.toCanvas(e);
        //trackpad pinch comes as wheel with ctrl and small deltas
        const factor = Math.exp(-e.deltaY * (e.ctrlKey ? 0.01 : 0.0015));
        this.zoomAt(this.canvasToImage(point), point, this.zoom() * factor);
    }

    private zoomAtCenter(zoom: number): void {
        const v = this.view;
        if (!v) return;
        const center = { x: v.cssW / 2, y: v.cssH / 2 };
        this.zoomAt(this.canvasToImage(center), center, zoom);
    }

    //sets the zoom so that imagePoint stays at canvasPoint (pinch center, mouse position)
    private zoomAt(imagePoint: Point, canvasPoint: Point, zoom: number): void {
        const v = this.view;
        if (!v) return;
        this.commitText(); //the text input would not follow the zoom
        zoom = clamp(zoom, 1, MAX_ZOOM);
        const scale = v.baseScale * zoom;
        const offsetX = canvasPoint.x - (imagePoint.x - v.region.x) * scale;
        const offsetY = canvasPoint.y - (imagePoint.y - v.region.y) * scale;
        this.pan = { x: offsetX - (v.cssW - v.region.w * scale) / 2, y: offsetY - (v.cssH - v.region.h * scale) / 2 };
        this.zoom.set(zoom);
        this.scheduleRender();
    }

    //the image may not be moved further than its edges (plus the stage padding)
    private clampPan(pan: Point, region: Rect, scale: number, cssW: number, cssH: number): Point {
        const limitX = Math.max(0, (region.w * scale - (cssW - STAGE_PADDING * 2)) / 2);
        const limitY = Math.max(0, (region.h * scale - (cssH - STAGE_PADDING * 2)) / 2);
        return { x: clamp(pan.x, -limitX, limitX), y: clamp(pan.y, -limitY, limitY) };
    }

    private startPinch(): void {
        const [a, b] = [...this.pointers.values()];
        const center = this.toCanvasFromClient({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
        this.commitText();
        this.pinch = { startDistance: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), startZoom: this.zoom(), anchor: this.canvasToImage(center) };
    }

    private updatePinch(): void {
        const [a, b] = [...this.pointers.values()];
        const center = this.toCanvasFromClient({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
        const zoom = this.pinch!.startZoom * Math.hypot(a.x - b.x, a.y - b.y) / this.pinch!.startDistance;
        //the image point under the fingers stays under them: zooming and panning in one gesture
        this.zoomAt(this.pinch!.anchor, center, zoom);
    }
    //#endregion

    //#region pointer handling
    onPointerDown(e: PointerEvent): void {
        const s = this.state();
        if (!s || !this.view || e.button > 1) return;
        this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        this.canvasRef().nativeElement.setPointerCapture(e.pointerId);
        if (this.pointers.size === 2) {
            //second finger: whatever the first one started becomes a pinch
            this.cancelDrag();
            this.startPinch();
            this.gestureLock = true;
            return;
        }
        if (this.pointers.size > 2 || this.gestureLock) return;
        if (this.textDraft()) {
            this.commitText();
            return;
        }
        if (e.button === 1 || this.spacePressed) {
            e.preventDefault();
            this.drag = { kind: 'pan', startClient: { x: e.clientX, y: e.clientY }, startPan: this.pan };
            return;
        }
        const p = this.toImage(e);
        if (this.tool() === 'crop') {
            const { target, rect, bounds, ratio } = this.editedFrame(s);
            const corner = this.hitCorner(p, rect);
            if (corner) {
                this.drag = {
                    kind: 'crop-resize', target, bounds, ratio, dirX: corner.dirX, dirY: corner.dirY,
                    anchor: { x: corner.dirX > 0 ? rect.x : rect.x + rect.w, y: corner.dirY > 0 ? rect.y : rect.y + rect.h }
                };
            } else if (this.inside(p, rect)) {
                this.drag = { kind: 'crop-move', target, bounds, start: p, startCrop: rect };
            }
            return;
        }
        const tool = this.markupTool();
        if (tool === 'select') {
            this.selectDown(p, s);
            if (this.drag?.kind === 'move') e.preventDefault(); //a double tap may focus the text input
            return;
        }
        const { w, h } = this.orientedSizeOf(s);
        if (tool === 'text') {
            //the text input opens on pointer up (iOS shows the keyboard only for a focus within the tap, see openText)
            this.drag = { kind: 'text', at: p };
            e.preventDefault(); //no focus change by the following mouse events
            return;
        }
        const width = strokeWidth(this.size(), w, h);
        this.drag = {
            kind: 'draw',
            annotation: tool === 'pen' ? { kind: 'pen', color: this.color(), width, points: [p] } : { kind: tool, color: this.color(), width, from: p, to: p }
        };
    }

    private selectDown(p: Point, s: EditorState): void {
        const selectedIndex = this.selected();
        const selected = this.selectedAnnotation();
        if (selectedIndex !== undefined && selected) {
            const handle = this.hitHandle(selected, p);
            if (handle) {
                this.drag = { kind: 'handle', index: selectedIndex, handle, original: selected };
                return;
            }
        }
        const index = this.hitTop(s, p);
        if (index === undefined) {
            this.selected.set(undefined);
            return;
        }
        const now = performance.now();
        const doubleTap = this.lastTap?.index === index && now - this.lastTap.time < DOUBLE_TAP_MS;
        this.lastTap = { index, time: now };
        this.selected.set(index);
        this.drag = { kind: 'move', index, start: p, original: s.annotations[index], moved: false, editText: doubleTap && s.annotations[index].kind === 'text' };
    }

    onPointerMove(e: PointerEvent): void {
        const s = this.state();
        if (!s || !this.view) return;
        if (this.pointers.has(e.pointerId)) this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (this.pinch && this.pointers.size >= 2) {
            this.updatePinch();
            return;
        }
        const p = this.toImage(e);
        if (!this.drag) {
            this.updateCursor(p, s);
            return;
        }
        switch (this.drag.kind) {
            case 'pan': {
                const v = this.view;
                this.pan = this.clampPan(
                    { x: this.drag.startPan.x + e.clientX - this.drag.startClient.x, y: this.drag.startPan.y + e.clientY - this.drag.startClient.y },
                    v.region, v.scale, v.cssW, v.cssH);
                break;
            }
            case 'crop-move': {
                const c = this.drag.startCrop;
                const b = this.drag.bounds;
                this.liveRect = {
                    ...c,
                    x: clamp(c.x + p.x - this.drag.start.x, b.x, b.x + b.w - c.w),
                    y: clamp(c.y + p.y - this.drag.start.y, b.y, b.y + b.h - c.h)
                };
                break;
            }
            case 'crop-resize':
                this.liveRect = this.resizeRect(this.drag, p);
                break;
            case 'draw': {
                const a = this.drag.annotation;
                if (a.kind === 'pen') {
                    const last = a.points[a.points.length - 1];
                    //skip points closer than ~1 screen pixel
                    if (Math.hypot(p.x - last.x, p.y - last.y) * this.view.scale >= 1) a.points.push(p);
                } else if (a.kind !== 'text') a.to = p;
                break;
            }
            case 'move': {
                const dx = p.x - this.drag.start.x;
                const dy = p.y - this.drag.start.y;
                //a tap (e.g. to select or double tap) must not move the annotation a little
                if (!this.drag.moved && Math.hypot(dx, dy) * this.view.scale < MOVE_THRESHOLD) return;
                this.drag.moved = true;
                this.liveAnnotation = { index: this.drag.index, annotation: translateAnnotation(this.drag.original, dx, dy) };
                break;
            }
            case 'handle':
                this.liveAnnotation = { index: this.drag.index, annotation: moveHandle(this.drag.original, this.drag.handle, p) };
                break;
        }
        this.scheduleRender();
    }

    onPointerUp(e: PointerEvent, cancelled = false): void {
        this.pointers.delete(e.pointerId);
        if (this.pinch || this.gestureLock) {
            //end of a pinch: the remaining finger does nothing until it is lifted too
            if (this.pointers.size < 2) this.pinch = undefined;
            if (this.pointers.size === 0) this.gestureLock = false;
            return;
        }
        const s = this.state();
        const drag = this.drag;
        this.drag = undefined;
        if (!s || !drag || !this.view) return;
        switch (drag.kind) {
            case 'text':
                if (!cancelled) {
                    const { w, h } = this.orientedSizeOf(s);
                    this.openText(drag.at, textSize(this.size(), w, h), this.color());
                }
                break;
            case 'draw': {
                const a = drag.annotation;
                const tooSmall = a.kind !== 'pen' && a.kind !== 'text' && Math.hypot(a.to.x - a.from.x, a.to.y - a.from.y) * this.view.scale < 4;
                if (!tooSmall) this.push({ ...s, annotations: [...s.annotations, a] });
                else this.scheduleRender();
                break;
            }
            case 'move':
            case 'handle': {
                const live = this.liveAnnotation;
                this.liveAnnotation = undefined;
                if (live && (drag.kind === 'handle' || drag.moved)) this.pushAnnotation(live.index, live.annotation);
                //double tap on a text: edit it (still within the tap, so iOS opens the keyboard)
                else if (drag.kind === 'move' && drag.editText && !cancelled) this.editSelectedText();
                this.scheduleRender();
                break;
            }
            case 'crop-move':
            case 'crop-resize':
                if (this.liveRect) {
                    const rect = this.liveRect;
                    this.liveRect = undefined;
                    this.push(drag.target === 'frame' ? { ...s, listingFrame: rect } : withValidFrame({ ...s, crop: rect }));
                }
                break;
        }
    }

    //drops what a single pointer started (unfinished stroke, crop move, ...)
    private cancelDrag(): void {
        this.drag = undefined;
        this.liveRect = undefined;
        this.liveAnnotation = undefined;
        this.scheduleRender();
    }

    //what the crop tab edits: the crop inside the whole image, or the 4:3 listing frame inside the crop
    private editedFrame(s: EditorState): { target: FrameTarget; rect: Rect; bounds: Rect; ratio?: number } {
        if (this.cropTarget() === 'frame') {
            return { target: 'frame', rect: s.listingFrame ?? fitAspect(s.crop, LISTING_ASPECT_RATIO), bounds: s.crop, ratio: LISTING_ASPECT_RATIO };
        }
        const { w, h } = this.orientedSizeOf(s);
        return { target: 'crop', rect: s.crop, bounds: { x: 0, y: 0, w, h }, ratio: ASPECT_RATIOS[this.aspect()] };
    }

    private resizeRect(drag: Extract<Drag, { kind: 'crop-resize' }>, p: Point): Rect {
        const { anchor, dirX, dirY, bounds: b, ratio } = drag;
        const maxW = dirX > 0 ? b.x + b.w - anchor.x : anchor.x - b.x;
        const maxH = dirY > 0 ? b.y + b.h - anchor.y : anchor.y - b.y;
        let w = clamp((p.x - anchor.x) * dirX, MIN_CROP, maxW);
        let h = clamp((p.y - anchor.y) * dirY, MIN_CROP, maxH);
        if (ratio) {
            if (w / h > ratio) w = h * ratio;
            else h = w / ratio;
        }
        return { x: dirX > 0 ? anchor.x : anchor.x - w, y: dirY > 0 ? anchor.y : anchor.y - h, w, h };
    }

    private hitCorner(p: Point, crop: Rect): { dirX: number; dirY: number } | undefined {
        const tolerance = HANDLE_HIT / this.view!.scale;
        for (const dirX of [-1, 1]) {
            for (const dirY of [-1, 1]) {
                const cx = dirX > 0 ? crop.x + crop.w : crop.x;
                const cy = dirY > 0 ? crop.y + crop.h : crop.y;
                if (Math.abs(p.x - cx) <= tolerance && Math.abs(p.y - cy) <= tolerance) return { dirX, dirY };
            }
        }
        return undefined;
    }

    //topmost (last drawn) annotation at p
    private hitTop(s: EditorState, p: Point): number | undefined {
        const tolerance = HANDLE_HIT / 2 / this.view!.scale;
        for (let i = s.annotations.length - 1; i >= 0; i--) {
            if (hitAnnotation(s.annotations[i], p, tolerance, this.textWidth)) return i;
        }
        return undefined;
    }

    private hitHandle(a: Annotation, p: Point): AnnotationHandle | undefined {
        const tolerance = HANDLE_HIT / this.view!.scale;
        return annotationHandles(a).find(h => Math.hypot(p.x - h.point.x, p.y - h.point.y) <= tolerance)?.handle;
    }

    private inside(p: Point, r: Rect): boolean {
        return p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h;
    }

    private updateCursor(p: Point, s: EditorState): void {
        let cursor = 'default';
        if (this.spacePressed) cursor = 'grab';
        else if (this.tool() === 'crop') {
            const rect = this.editedFrame(s).rect;
            const corner = this.hitCorner(p, rect);
            if (corner) cursor = corner.dirX === corner.dirY ? 'nwse-resize' : 'nesw-resize';
            else if (this.inside(p, rect)) cursor = 'move';
        } else if (this.markupTool() === 'select') {
            const selected = this.selectedAnnotation();
            if (selected && this.hitHandle(selected, p)) cursor = 'crosshair';
            else if (this.hitTop(s, p) !== undefined) cursor = 'move';
        } else cursor = this.markupTool() === 'text' ? 'text' : 'crosshair';
        this.canvasRef().nativeElement.style.cursor = cursor;
    }

    private toCanvasFromClient(client: Point): Point {
        const rect = this.canvasRef().nativeElement.getBoundingClientRect();
        return { x: client.x - rect.left, y: client.y - rect.top };
    }

    private toCanvas(e: MouseEvent): Point {
        return this.toCanvasFromClient({ x: e.clientX, y: e.clientY });
    }

    private canvasToImage(point: Point): Point {
        const v = this.view!;
        return { x: (point.x - v.offsetX) / v.scale + v.region.x, y: (point.y - v.offsetY) / v.scale + v.region.y };
    }

    private toImage(e: PointerEvent): Point {
        return this.canvasToImage(this.toCanvas(e));
    }

    private readonly textWidth = (a: TextAnnotation): number => {
        this.measureContext ??= document.createElement('canvas').getContext('2d')!;
        this.measureContext.font = textFont(a.size);
        return this.measureContext.measureText(a.text).width;
    };
    //#endregion

    //#region text
    //new text at click, or editing an existing one (existing: its index and text)
    private openText(click: Point, size: number, color: string, existing?: { index: number; text: string }): void {
        const s = this.state()!;
        //keep the line inside the visible (cropped) image vertically
        const y = existing ? click.y : clamp(click.y, s.crop.y, s.crop.y + s.crop.h - size * 1.1);
        this.textDraft.set({ click, size, color, at: { x: click.x, y }, left: 0, top: 0, width: 0, fontPx: 0, editIndex: existing?.index });
        this.layoutText(existing?.text ?? '');
        //iOS opens the keyboard only if focus() is called synchronously within the tap: the input always exists,
        //it gets its position right here (the bindings follow with the next change detection) and is focused immediately
        const input = this.textInputRef().nativeElement;
        const box = this.textBox()!;
        input.value = existing?.text ?? '';
        input.style.left = box.left + 'px';
        input.style.top = box.top + 'px';
        input.style.width = box.width + 'px';
        input.style.transform = box.transform;
        input.style.color = box.color;
        input.focus({ preventScroll: true });
        this.scheduleRender(); //an edited text is hidden while its input is open
    }

    //the box grows with the text; once it reaches the right edge, the text moves left so it stays inside the image
    layoutText(text: string): void {
        const draft = this.textDraft();
        const s = this.state();
        const v = this.view;
        if (!draft || !s || !v) return;
        this.measureContext ??= document.createElement('canvas').getContext('2d')!;
        this.measureContext.font = textFont(draft.size);
        const margin = draft.size * 0.1; //room for the outline
        const width = Math.max(this.measureContext.measureText(text).width, draft.size * 0.5);
        const x = Math.max(s.crop.x + margin, Math.min(draft.click.x, s.crop.x + s.crop.w - margin - width));
        this.textDraft.set({
            ...draft,
            at: { x, y: draft.at.y },
            left: v.offsetX + (x - v.region.x) * v.scale,
            top: v.offsetY + (draft.at.y - v.region.y) * v.scale,
            width: width * v.scale,
            fontPx: draft.size * v.scale
        });
    }

    onTextKeydown(e: KeyboardEvent): void {
        e.stopPropagation(); //no editor shortcuts while typing
        if (e.key === 'Enter') this.commitText();
        else if (e.key === 'Escape') this.closeText();
    }

    commitText(): void {
        const draft = this.textDraft();
        const text = this.textInputRef().nativeElement.value.trim();
        this.closeText();
        const s = this.state();
        if (!draft || !s) return;
        if (draft.editIndex !== undefined) {
            const a = s.annotations[draft.editIndex];
            //an emptied text is removed
            if (a?.kind === 'text' && a.text !== text) this.pushAnnotation(draft.editIndex, text ? { ...a, text, at: draft.at } : undefined);
            if (!text) this.selected.set(undefined);
            return;
        }
        if (text) this.push({ ...s, annotations: [...s.annotations, { kind: 'text', color: draft.color, size: draft.size, at: draft.at, text }] });
    }

    //hides the (always existing) input again; blur closes the keyboard on phones (the blur handler finds no draft any more)
    private closeText(): void {
        const input = this.textInputRef().nativeElement;
        const wasOpen = !!this.textDraft();
        this.textDraft.set(undefined);
        input.value = '';
        if (document.activeElement === input) input.blur();
        if (wasOpen) this.scheduleRender();
    }
    //#endregion

    onKeydown(e: KeyboardEvent): void {
        const target = e.target as HTMLElement | null;
        if (e.code === 'Space' && target?.tagName !== 'INPUT' && target?.tagName !== 'BUTTON') {
            this.spacePressed = true;
            e.preventDefault();
            return;
        }
        if ((e.key === 'Delete' || e.key === 'Backspace') && this.selected() !== undefined && target?.tagName !== 'INPUT') {
            this.deleteSelected();
            e.preventDefault();
            return;
        }
        if (e.key === 'Escape' && this.selected() !== undefined) {
            this.selected.set(undefined);
            return;
        }
        if (!(e.ctrlKey || e.metaKey)) return;
        const key = e.key.toLowerCase();
        if (key === 'z' && !e.shiftKey) this.undo();
        else if (key === 'y' || (key === 'z' && e.shiftKey)) this.redo();
        else return;
        e.preventDefault();
    }

    onKeyup(e: KeyboardEvent): void {
        if (e.code === 'Space') this.spacePressed = false;
    }

    //#region rendering
    private orientedSizeOf(s: EditorState): { w: number; h: number } {
        return orientedSize(this.source!.width, this.source!.height, s.quarterTurns);
    }

    private orientedCanvas(s: EditorState): HTMLCanvasElement {
        const key = `${s.quarterTurns}|${s.flipH}|${s.flipV}`;
        if (this.oriented?.key !== key) this.oriented = { key, canvas: renderOriented(this.source!, s.quarterTurns, s.flipH, s.flipV) };
        return this.oriented.canvas;
    }

    private resizeCanvas(): void {
        const stage = this.stageRef().nativeElement;
        const canvas = this.canvasRef().nativeElement;
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.round(stage.clientWidth * dpr);
        canvas.height = Math.round(stage.clientHeight * dpr);
        this.render();
    }

    private scheduleRender(): void {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.render());
    }

    private render(): void {
        const s = this.state();
        const canvas = this.canvasRef().nativeElement;
        const ctx = canvas.getContext('2d')!;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (!s || !this.source) return;

        const dpr = window.devicePixelRatio || 1;
        const cssW = canvas.width / dpr;
        const cssH = canvas.height / dpr;
        const cropMode = this.tool() === 'crop';
        const image = this.orientedCanvas(s);
        //while cropping the whole image is shown, otherwise (incl. the listing frame) only the cropped part.
        //The region does not change during a drag, so pointer mapping stays stable.
        const region: Rect = cropMode && this.cropTarget() === 'photo' ? { x: 0, y: 0, w: image.width, h: image.height } : s.crop;
        const baseScale = Math.min((cssW - STAGE_PADDING * 2) / region.w, (cssH - STAGE_PADDING * 2) / region.h);
        if (baseScale <= 0) return;
        const scale = baseScale * this.zoom();
        this.pan = this.clampPan(this.pan, region, scale, cssW, cssH);
        const offsetX = (cssW - region.w * scale) / 2 + this.pan.x;
        const offsetY = (cssH - region.h * scale) / 2 + this.pan.y;
        this.view = { scale, baseScale, offsetX, offsetY, region, cssW, cssH };

        ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * (offsetX - region.x * scale), dpr * (offsetY - region.y * scale));
        ctx.save();
        ctx.beginPath();
        ctx.rect(region.x, region.y, region.w, region.h);
        ctx.clip();
        ctx.drawImage(image, 0, 0);
        const editIndex = this.textDraft()?.editIndex;
        s.annotations.forEach((a, i) => {
            if (i === editIndex) return; //its text input is shown instead
            drawAnnotation(ctx, this.liveAnnotation?.index === i ? this.liveAnnotation.annotation : a);
        });
        if (this.drag?.kind === 'draw') drawAnnotation(ctx, this.drag.annotation);
        ctx.restore();

        if (cropMode) this.renderCropFrame(ctx, region, this.liveRect ?? this.editedFrame(s).rect, scale);
        else if (this.markupTool() === 'select' && editIndex === undefined) this.renderSelection(ctx, s, scale);
    }

    //dashed box around the selected annotation, round handles to drag (arrow ends, corners)
    private renderSelection(ctx: CanvasRenderingContext2D, s: EditorState, scale: number): void {
        const index = this.selected();
        if (index === undefined || !s.annotations[index]) return;
        const a = this.liveAnnotation?.index === index ? this.liveAnnotation.annotation : s.annotations[index];
        const px = 1 / scale;
        const padding = 6 * px + ('width' in a ? a.width / 2 : 0);
        const b = annotationBounds(a, this.textWidth);
        ctx.save();
        ctx.lineWidth = 1.5 * px;
        ctx.setLineDash([6 * px, 4 * px]);
        ctx.strokeStyle = '#ffffff';
        ctx.strokeRect(b.x - padding, b.y - padding, b.w + padding * 2, b.h + padding * 2);
        ctx.lineDashOffset = 5 * px;
        ctx.strokeStyle = '#000000';
        ctx.strokeRect(b.x - padding, b.y - padding, b.w + padding * 2, b.h + padding * 2);
        ctx.setLineDash([]);
        for (const h of annotationHandles(a)) {
            ctx.beginPath();
            ctx.arc(h.point.x, h.point.y, 7 * px, 0, Math.PI * 2);
            ctx.fillStyle = '#ffffff';
            ctx.fill();
            ctx.lineWidth = 2 * px;
            ctx.strokeStyle = '#1e88e5';
            ctx.stroke();
        }
        ctx.restore();
    }

    //frame with handles inside region, the rest of the region dimmed
    private renderCropFrame(ctx: CanvasRenderingContext2D, region: Rect, crop: Rect, scale: number): void {
        const px = 1 / scale; //one screen pixel in image coordinates
        ctx.save();
        ctx.fillStyle = 'rgba(0,0,0,0.55)';
        ctx.beginPath();
        ctx.rect(region.x, region.y, region.w, region.h);
        ctx.rect(crop.x, crop.y, crop.w, crop.h);
        ctx.fill('evenodd');

        //rule of thirds
        ctx.strokeStyle = 'rgba(255,255,255,0.45)';
        ctx.lineWidth = px;
        ctx.beginPath();
        for (const f of [1 / 3, 2 / 3]) {
            ctx.moveTo(crop.x + crop.w * f, crop.y);
            ctx.lineTo(crop.x + crop.w * f, crop.y + crop.h);
            ctx.moveTo(crop.x, crop.y + crop.h * f);
            ctx.lineTo(crop.x + crop.w, crop.y + crop.h * f);
        }
        ctx.stroke();

        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 2 * px;
        ctx.strokeRect(crop.x, crop.y, crop.w, crop.h);

        //corner handles
        const len = Math.min(18 * px, crop.w / 3, crop.h / 3);
        ctx.lineWidth = 4 * px;
        ctx.lineCap = 'square';
        ctx.beginPath();
        for (const [cx, cy, dx, dy] of [[crop.x, crop.y, 1, 1], [crop.x + crop.w, crop.y, -1, 1], [crop.x, crop.y + crop.h, 1, -1], [crop.x + crop.w, crop.y + crop.h, -1, -1]]) {
            ctx.moveTo(cx, cy + dy * len);
            ctx.lineTo(cx, cy);
            ctx.lineTo(cx + dx * len, cy);
        }
        ctx.stroke();
        ctx.restore();
    }
    //#endregion

    //#region save
    save(asCopy: boolean): void {
        this.commitText();
        const s = this.state();
        if (!s || !this.isDirty() || this.saving()) return;
        const original = this.data.file;
        const initial = this.history()[0];
        //listing frame relative to the saved file (= the crop)
        const valid = validFrame(s);
        const frame: ImageFrame | null = valid
            ? { x: (valid.x - s.crop.x) / s.crop.w, y: (valid.y - s.crop.y) / s.crop.h, w: valid.w / s.crop.w, h: valid.h / s.crop.h }
            : null;
        const pixelsChanged = s.quarterTurns !== initial.quarterTurns || s.flipH !== initial.flipH || s.flipV !== initial.flipV
            || JSON.stringify(s.crop) !== JSON.stringify(initial.crop) || JSON.stringify(s.annotations) !== JSON.stringify(initial.annotations);
        if (!pixelsChanged) {
            //only frame/fit: the file stays as it is (a copy gets the same content as its own file)
            this.dialogRef.close({ file: asCopy ? new File([original], fileName(original.name, original.type, '-copy'), { type: original.type }) : undefined, asCopy, frame, fit: s.fit });
            return;
        }
        this.saving.set(true);

        const image = this.orientedCanvas(s);
        const crop = { x: Math.round(s.crop.x), y: Math.round(s.crop.y), w: Math.round(s.crop.w), h: Math.round(s.crop.h) };
        const out = document.createElement('canvas');
        out.width = crop.w;
        out.height = crop.h;
        const ctx = out.getContext('2d')!;
        ctx.translate(-crop.x, -crop.y);
        ctx.drawImage(image, 0, 0);
        s.annotations.forEach(a => drawAnnotation(ctx, a));

        //keep the original format, so file name and content type match
        const type = OUTPUT_TYPES.includes(original.type) ? original.type : 'image/jpeg';
        out.toBlob(blob => {
            this.saving.set(false);
            if (!blob) {
                console.error('Exporting image failed');
                return;
            }
            const name = fileName(original.name, type, asCopy ? '-markup' : '');
            this.dialogRef.close({ file: new File([blob], name, { type }), asCopy, frame, fit: s.fit });
        }, type, 0.92);
    }

    cancel(): void {
        this.dialogRef.close(undefined);
    }
    //#endregion
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max);
}

//the listing frame if it is (still) inside the crop, otherwise none (whole image centered)
function validFrame(s: EditorState): Rect | null {
    const f = s.listingFrame;
    const c = s.crop;
    const eps = 0.5; //image pixels
    if (!f || f.x < c.x - eps || f.y < c.y - eps || f.x + f.w > c.x + c.w + eps || f.y + f.h > c.y + c.h + eps) return null;
    return f;
}

function withValidFrame(s: EditorState): EditorState {
    return { ...s, listingFrame: validFrame(s) };
}

function fileName(name: string, type: string, suffix: string): string {
    const dot = name.lastIndexOf('.');
    const base = dot > 0 ? name.substring(0, dot) : name;
    const originalExt = dot > 0 ? name.substring(dot) : '';
    const ext = type === 'image/png' ? '.png' : type === 'image/webp' ? '.webp' : (/\.jpe?g$/i.test(originalExt) ? originalExt : '.jpg');
    return base + suffix + ext;
}
